import { useAppSelector } from "@app/hooks";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import useSurveyState from "@hooks/useSurveyState";
import { useContactActions } from "@survey-cover/contacts/hooks/useContactActions";
import type {
  ContactFormProps,
  ContactFormValues,
} from "@survey-types/survey.contacts.types";
import { selectEditingContactId } from "@survey/surveySelectors";
import { setContactEditingId } from "@survey/surveySlice";
import { useState, type ChangeEvent } from "react";

/**
 * Contact form component that handles both create and edit modes.
 * React Compiler handles optimization automatically.
 */
export const ContactForm = ({
  initialValues = {
    name: "",
    title: "",
    email: "",
    phoneNumber: "",
    licenseNo: "",
  },
  contact,
  onSuccess,
  isLoading: externalLoading = false,
}: ContactFormProps) => {
  const { dispatch } = useSurveyState();
  const editingContactId = useAppSelector(selectEditingContactId);
  const { handleCreateContact, handleUpdateContact, isAdding, isUpdating } =
    useContactActions();

  const [values, setValues] = useState<ContactFormValues>(initialValues);

  const isLoading = externalLoading || isAdding || isUpdating;
  const isEditing = !!editingContactId;

  const handleSave = async () => {
    const contactFields: (keyof ContactFormValues)[] = ['name', 'email', 'phoneNumber', 'title', 'licenseNo'];
    const hasEmptyFields = contactFields.every(field => values[field].trim() === '');

    if (hasEmptyFields) {
      return;
    }
    try {
      if (isEditing && contact?.contactId) {
        await handleUpdateContact(contact.contactId, values);
      } else {
        await handleCreateContact(values);
      }
      onSuccess?.();
    } catch {
      // Error already logged in hooks
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      dispatch(setContactEditingId(null));
    } else {
      onSuccess?.(); // Close the form
    }
  };

  const handleFieldChange =
    (field: keyof ContactFormValues) =>
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setValues((prev) => ({ ...prev, [field]: newValue }));
      };

  const saveLabel = isEditing ? "Update" : "Save";

  return (
    <div className="flex flex-col gap-4 mt-4 w-full broker-team-contact-form">
      <EmeraldInputV2
        id="SC-SurveyContactName"
        label="Name"
        value={values.name}
        onChange={handleFieldChange("name")}
      />
      <EmeraldInputV2
        id="SC-SurveyContactTitle"
        label="Title"
        value={values.title}
        onChange={handleFieldChange("title")}
      />
      <EmeraldInputV2
        id="SC-SurveyContactLicense"
        label="License"
        value={values.licenseNo}
        onChange={handleFieldChange("licenseNo")}
      />
      <EmeraldInputV2
        id="SC-SurveyContactEmail"
        label="Email"
        value={values.email}
        onChange={handleFieldChange("email")}
      />
      <EmeraldInputV2
        id="SC-SurveyContactPhone"
        label="Phone #"
        value={values.phoneNumber}
        onChange={handleFieldChange("phoneNumber")}
      />

      <div className="flex justify-end gap-4 mt-2">
        <EmeraldButton
          id="SC-CancelContact"
          label="Cancel"
          type="TextButton"
          onClick={handleCancel}
          disabled={isLoading}
        />
        <EmeraldButton
          id="SC-SaveContact"
          label={saveLabel}
          type="Primary"
          onClick={handleSave}
          className={isLoading ? "pointer-events-none" : ""}
          showSpinner={isLoading}
        />
      </div>
    </div>
  );
};
